import type { ReactNode } from "react";
import type { SimpleToastOptions, ToastId } from "./types";

/**
 * Module-level store for simple (non-execution) toasts, rendered as pills in
 * the same bottom-center stack as execution toasts (v1 parity — v1's
 * SimplePillNotification shared the center stack). Living outside React means
 * `toast.success(...)` fired before the provider mounts is never lost — the
 * provider reads the store via useSyncExternalStore when it appears.
 */

export type SimpleToastKind =
  | "success"
  | "error"
  | "info"
  | "warning"
  | "loading";

export interface SimpleToastRecord {
  action: SimpleToastOptions["action"] | null;
  createdAtMs: number;
  description: ReactNode | null;
  /** Infinity = never auto-dismisses (loading). */
  durationMs: number;
  id: ToastId;
  kind: SimpleToastKind;
  message: string;
}

const DEFAULT_SIMPLE_TOAST_DURATION_MS = 4_000;
const ERROR_SIMPLE_TOAST_DURATION_MS = 8_000;

let records: readonly SimpleToastRecord[] = [];
const listeners = new Set<() => void>();
let idCounter = 0;

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function getSimpleToasts(): readonly SimpleToastRecord[] {
  return records;
}

export function subscribeSimpleToasts(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function showSimpleToast(
  kind: SimpleToastKind,
  message: string,
  options?: SimpleToastOptions,
): ToastId {
  idCounter += 1;
  const id = options?.id ?? `simple-toast-${idCounter}`;
  const record: SimpleToastRecord = {
    action: options?.action ?? null,
    createdAtMs: Date.now(),
    description: options?.description ?? null,
    durationMs:
      options?.duration ??
      (kind === "loading"
        ? Infinity
        : kind === "error"
          ? ERROR_SIMPLE_TOAST_DURATION_MS
          : DEFAULT_SIMPLE_TOAST_DURATION_MS),
    id,
    kind,
    message,
  };

  // Caller-supplied ids upsert in place (dedupe for repeat warnings).
  const existingIndex = records.findIndex((entry) => entry.id === id);
  records =
    existingIndex >= 0
      ? records.map((entry, index) => (index === existingIndex ? record : entry))
      : [record, ...records];
  emit();
  return id;
}

/** No id → clear every simple toast. */
export function dismissSimpleToast(id?: ToastId): void {
  const nextRecords =
    id === undefined ? [] : records.filter((entry) => entry.id !== id);
  if (nextRecords.length === records.length && id !== undefined) {
    return;
  }
  records = nextRecords;
  emit();
}
